import Swiper from "swiper";
import { Autoplay } from "swiper/modules";
import "swiper/css";

const desktopQuery = window.matchMedia("(min-width: 800px)");
const reducedMotionQuery = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
);

const setDetailPosition = (section, card, detail) => {
  if (!desktopQuery.matches) {
    detail.style.removeProperty("--voice-detail-inline");
    detail.style.removeProperty("--voice-detail-block");
    detail.classList.remove("is-left");
    return;
  }

  const sectionRect = section.getBoundingClientRect();
  const cardRect = card.getBoundingClientRect();
  const detailWidth = Math.min(520, sectionRect.width - 40);
  const gap = 17.6;
  const spaceOnRight = sectionRect.right - cardRect.right;
  const placeOnLeft = spaceOnRight < detailWidth + gap;
  const inlinePosition = placeOnLeft
    ? cardRect.left - sectionRect.left - detailWidth - gap
    : cardRect.right - sectionRect.left + gap;

  detail.style.setProperty(
    "--voice-detail-inline",
    `${Math.max(20, inlinePosition)}px`,
  );
  detail.style.setProperty(
    "--voice-detail-block",
    `${cardRect.top - sectionRect.top + cardRect.height / 2}px`,
  );
  detail.classList.toggle("is-left", placeOnLeft);
};

const initVoice = (section) => {
  const slider = section.querySelector(".js-voice-slider");
  const cards = [...section.querySelectorAll(".js-voice-card")];
  const detail = section.querySelector(".js-voice-detail");
  const voiceCount = Number(section.dataset.voiceCount);

  if (!slider || !detail || cards.length === 0 || !voiceCount) {
    return;
  }

  const canMove = voiceCount > 1;
  const canAutoplay = canMove && !reducedMotionQuery.matches;
  let currentCard = cards[0];
  let contentSwitchTimer;

  const swiper = new Swiper(slider, {
    modules: [Autoplay],
    slidesPerView: 1.4,
    centeredSlides: true,
    spaceBetween: 12,
    speed: reducedMotionQuery.matches ? 0 : 2000,
    loop: canMove,
    slideToClickedSlide: false,
    watchSlidesProgress: true,
    autoplay: canAutoplay
      ? {
          delay: 3200,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }
      : false,
    breakpoints: {
      600: {
        slidesPerView: Math.min(2, voiceCount),
        centeredSlides: false,
        spaceBetween: 16,
      },
      960: {
        slidesPerView: Math.min(3, voiceCount),
        centeredSlides: false,
        spaceBetween: 16,
      },
      1320: {
        slidesPerView: Math.min(4, voiceCount),
        centeredSlides: false,
        spaceBetween: 16,
      },
    },
  });

  const setExpanded = (activeCard) => {
    for (const card of cards) {
      const isCurrent = card === activeCard;
      card.classList.toggle("is-current", isCurrent);
      card.setAttribute("aria-expanded", String(isCurrent));
    }
  };

  const replaceDetailContent = (card) => {
    const item = card.closest(".p-topVoice__item");
    const template = item?.querySelector(".js-voice-template");

    if (!template) {
      return false;
    }

    currentCard = card;
    detail.replaceChildren(template.content.cloneNode(true));
    return true;
  };

  const switchDetailContent = (card) => {
    window.clearTimeout(contentSwitchTimer);

    if (reducedMotionQuery.matches) {
      replaceDetailContent(card);
      return;
    }

    detail.classList.add("is-switching");
    contentSwitchTimer = window.setTimeout(() => {
      replaceDetailContent(card);
      requestAnimationFrame(() => {
        detail.classList.remove("is-switching");
      });
    }, 160);
  };

  const closeDetail = () => {
    detail.classList.remove("is-open");
    for (const card of cards) {
      card.setAttribute("aria-expanded", "false");
    }

    if (canAutoplay) {
      swiper.autoplay.start();
    }
  };

  const openDetail = (card) => {
    if (!replaceDetailContent(card)) {
      return;
    }

    detail.classList.add("is-open");
    setExpanded(card);
    setDetailPosition(section, card, detail);

    if (canAutoplay) {
      swiper.autoplay.stop();
    }
  };

  for (const card of cards) {
    card.addEventListener("click", () => {
      if (!desktopQuery.matches) {
        const slide = card.closest(".swiper-slide");
        const slideIndex = swiper.slides.indexOf(slide);

        if (slideIndex >= 0 && slideIndex !== swiper.activeIndex) {
          swiper.slideTo(slideIndex);
        }

        return;
      }

      if (detail.classList.contains("is-open") && currentCard === card) {
        closeDetail();
        return;
      }

      openDetail(card);
    });
  }

  swiper.on("slideChangeTransitionStart", () => {
    if (!desktopQuery.matches) {
      return;
    }

    if (detail.classList.contains("is-open")) {
      closeDetail();
    }
  });

  swiper.on("slideChangeTransitionEnd", () => {
    if (desktopQuery.matches || detail.classList.contains("is-open")) {
      return;
    }

    const activeCard =
      swiper.slides[swiper.activeIndex]?.querySelector(".js-voice-card");

    if (activeCard) {
      switchDetailContent(activeCard);
    }
  });

  section.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && detail.classList.contains("is-open")) {
      closeDetail();
      currentCard.focus();
    }
  });

  window.addEventListener(
    "resize",
    () => {
      if (detail.classList.contains("is-open")) {
        setDetailPosition(section, currentCard, detail);
      }
    },
    { passive: true },
  );
};

export const initVoices = () => {
  for (const section of document.querySelectorAll(".js-voice")) {
    initVoice(section);
  }
};
